<template>
  <v-row justify="center" align="center" no-gutters class="flex-wrap w-100">
    <v-card
      v-for="(chart, index) in charts"
      :key="`chart_${index}`"
      class="pa-2 ma-1 flex-grow-1 small-chart"
      outlined elevation="0" rounded="0"
    >
      <CubeQueryRenderer
        :filters="filters"
        :dateRange="dateRange"
        :defaultQuery="chart.defaultQuery"
        :componentName="chart.componentName"
        :timeDimension="chart.defaultTimeDimension"
        :fullView="false"
        :style="chart.chartWidth
          ? { width: chart.chartWidth }
          : { minWidth: '400px', maxWidth: '600px', width: '100%', flex: '1 1 400px' }"
        class="mx-auto"
        v-bind="currencyBindings(chart)"
        v-on="currencyListeners(chart)"
      />
      <v-card-actions class="justify-center pa-0">
        <v-btn icon small @click="openDialog(chart)">
          <v-icon>mdi-magnify</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
    <template v-if="pdfMode">
      <v-card
        v-for="(chart, index) in charts"
        :key="`full_chart_${index}`"
        class="pa-2 ma-1 flex-grow-1 pdf-mode-chart"
        outlined elevation="0" rounded="0"
        style="--full-view-chart-height: 600px; --full-view-chart-width: 50vw; position: absolute; top: -9999px; left: -9999px; width: 1200px;"
      >
        <CubeQueryRenderer
          :filters="filters"
          :dateRange="dateRange"
          :defaultQuery="chart.defaultQuery"
          :componentName="chart.componentName"
          :timeDimension="chart.defaultTimeDimension"
          :fullView="true"
          :style="{ width: '100%' }"
          class="mx-auto"
          v-bind="currencyBindings(chart)"
          v-on="currencyListeners(chart)"
        />
      </v-card>
    </template>
    <v-dialog v-model="dialog" max-width="90%" max-height="90%">
      <v-card style="--full-view-chart-height: 75vh; --full-view-chart-width: 75vw;">
        <v-card-actions class="justify-end pa-2">
          <v-btn icon @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-actions>
        <v-card-text>
          <CubeQueryRenderer
            v-if="dialog"
            :filters="filters"
            :dateRange="dateRange"
            :defaultQuery="currentChart.defaultQuery"
            :componentName="currentChart.componentName"
            :timeDimension="currentChart.defaultTimeDimension"
            :fullView="true"
            v-bind="currencyBindings(currentChart)"
            v-on="currencyListeners(currentChart)"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { InsightsEventBus } from '@/utils/insightsPdfUtils'
import { CHART_COMPONENTS } from '@/helpers/insightsCharts'

export default {
  name: 'ChartGrid',
  props: {
    charts: Array,
    filters: Array,
    dateRange: Array,
    pdfMode: Boolean
  },
  data () {
    return {
      dialog: false,
      currentChart: null,
      finishedCharts: [],
      expectedChartNames: [],
      selectedCurrencies: {}
    }
  },
  computed: {
    allChartsFinished () {
      const finished = this.expectedChartNames.every(name =>
        this.finishedCharts.includes(name)
      )
      return finished
    }
  },
  methods: {
    openDialog (chart) {
      this.currentChart = chart
      this.dialog = true
    },
    usesCurrencySelect (chart) {
      return chart.componentName === CHART_COMPONENTS.CONTRACTS_SIGNED_AND_ADJUSTED_PRICES
    },
    currencyBindings (chart) {
      if (!this.usesCurrencySelect(chart)) return {}

      return {
        selectedCurrency: this.selectedCurrencies[chart.componentName] || null
      }
    },
    currencyListeners (chart) {
      if (!this.usesCurrencySelect(chart)) return {}

      return {
        'update:selectedCurrency': currency => this.setSelectedCurrency(chart.componentName, currency)
      }
    },
    setSelectedCurrency (componentName, currency) {
      if (this.selectedCurrencies[componentName] === currency) return
      this.$set(this.selectedCurrencies, componentName, currency)
    },
    handleChartFinished (data) {
      const chart = this.charts.find(c => c.componentName === data.componentName)
      if (!chart) return

      if (data.pdfMode && this.pdfMode && this.expectedChartNames.includes(data.componentName)) {
        if (data.noData) {
          this.expectedChartNames = this.expectedChartNames.filter(
            name => name !== data.componentName
          )
        } else {
          if (!this.finishedCharts.includes(data.componentName)) {
            this.finishedCharts.push(data.componentName)
          }
        }

        if (this.allChartsFinished) {
          InsightsEventBus.$emit('all-charts-ready-for-pdf')
        }
      }
    }
  },
  mounted () {
    this.$nextTick(() => {
      window.dispatchEvent(new Event('resize'))
    })

    InsightsEventBus.$on('chart-finished', this.handleChartFinished)
  },
  beforeDestroy () {
    InsightsEventBus.$off('chart-finished', this.handleChartFinished)
  },
  watch: {
    pdfMode (newVal) {
      if (newVal) {
        this.finishedCharts = []
        this.expectedChartNames = this.charts.map(chart => chart.componentName)
      }
      this.$nextTick(() => {
        window.dispatchEvent(new Event('resize'))
      })
    }
  }
}
</script>
